'use client';

import { Table } from '@tanstack/react-table';
import { Download, Pause } from 'lucide-react';
import * as React from 'react';
import { useToast } from '@/components/ui/use-toast';
import {
  DataTableActionBar,
  DataTableActionBarAction,
  DataTableActionBarSelection,
} from '@/components/data-table/data-table-action-bar';
import { Separator } from '@/components/ui/separator';
import { formatMoney } from "@/lib/money";
import { useFormatDate } from '@/hooks/use-format-date';
import { Subscription } from '@/lib/api/subscriptions';
import { PauseSubscriptionsBulkActionModal } from '@/components/subscriptions/bulkAction/pause-subscriptions';
import { UpdateBillingCycleBulkActionModal } from '@/components/subscriptions/bulkAction/update-billing-cycle';
import { ChangeProcessorBulkActionModal } from '@/components/subscriptions/bulkAction/change-processor';
import { CancelSubscriptionsBulkActionModal } from '@/components/subscriptions/bulkAction/bulk-cancel';
import { BulkResumeSubscriptionsBulkActionModal } from '@/components/subscriptions/bulkAction/bulk-resume';
import { UpdatePriceBulkActionModal } from '@/components/subscriptions/bulkAction/update-price';
import { UpdateShippingRateBulkActionModal } from '@/components/subscriptions/bulkAction/update-shipping-rate';
import { UpdateQuantityBulkActionModal } from '@/components/subscriptions/bulkAction/update-quantity';
import { SetTagadaVaultBulkActionModal } from '@/components/subscriptions/bulkAction/set-tagada-vault';
import { RetryPaymentBulkActionModal } from '@/components/subscriptions/bulkAction/retry-payment';

interface SubscriptionsTableActionBarProps {
  table: Table<Subscription>;
}

export enum SubscriptionsTableActionBarAction {
  Export = 'export',
  BulkPause = 'bulkPause',
}

export function SubscriptionsTableActionBar({ table }: SubscriptionsTableActionBarProps) {
  const rows = table.getFilteredSelectedRowModel().rows;
  const [isPending, startTransition] = React.useTransition();
  const [currentAction, setCurrentAction] = React.useState<SubscriptionsTableActionBarAction | null>(null);
  const { toast } = useToast();
  const { format: formatTz, isReady: isTzReady } = useFormatDate();

  const getIsActionPending = React.useCallback(
    (action: SubscriptionsTableActionBarAction) => isPending && currentAction === action,
    [isPending, currentAction],
  );

  // Function to handle exporting selected subscriptions data
  const onExport = React.useCallback(() => {
    setCurrentAction(SubscriptionsTableActionBarAction.Export);
    startTransition(() => {
      try {
        // Get selected data
        const data = rows.map((row) => {
          const { id, customerEmail, status, sortableAmount, currency, nextBillingDate, createdAt } =
            row.original;

          // Format dates in the account's timezone.
          const nextBilling = nextBillingDate ? formatTz(nextBillingDate, 'yyyy-MM-dd') : 'N/A';
          const created = createdAt ? formatTz(createdAt, 'yyyy-MM-dd') : 'N/A';

          // Format amount using formatMoney
          const formattedAmount = sortableAmount ? formatMoney(sortableAmount, currency || 'USD') : '$0.00';

          // Prepare CSV row
          return {
            ID: id,
            Customer: customerEmail || 'N/A',
            Status: status,
            Amount: formattedAmount,
            'Next Billing': nextBilling,
            Created: created,
          };
        });

        // Convert to CSV
        const headers = Object.keys(data[0]);
        const csvRows = [headers.join(',')];

        for (const row of data) {
          const values = headers.map((header) => {
            const value = (row as Record<string, string>)[header] || '';
            const escaped = value.replace(/"/g, '""');
            return `"${escaped}"`;
          });
          csvRows.push(values.join(','));
        }

        const csvContent = csvRows.join('\n');

        // Create download
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `selected-subscriptions-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: 'Export successful',
          description: `${data.length} subscription records exported to CSV`,
        });
      } catch (error) {
        console.error('Export error:', error);
        toast({
          title: 'Export failed',
          description: 'There was an error exporting the data',
          variant: 'destructive',
        });
      }
    });
  }, [rows, toast, formatTz]);

  const subscriptionIds = rows.map((row) => row.original.id);
  const selectedSubscriptions = rows.map((row) => row.original);

  return (
    <>
      <DataTableActionBar table={table} visible={rows.length > 0}>
        <DataTableActionBarSelection table={table} />
        <Separator orientation="vertical" className="hidden data-[orientation=vertical]:h-5 sm:block" />
        <div className="flex items-center gap-1.5">
          <DataTableActionBarAction
            tooltip="Export selected"
            isPending={getIsActionPending(SubscriptionsTableActionBarAction.Export)}
            onClick={onExport}
            disabled={!isTzReady}
          >
            <Download className="mr-2 h-4 w-4" />
            Export
          </DataTableActionBarAction>
          <PauseSubscriptionsBulkActionModal subscriptionIds={subscriptionIds} />
          <UpdateBillingCycleBulkActionModal subscriptionIds={subscriptionIds} />
          <UpdatePriceBulkActionModal subscriptionIds={subscriptionIds} subscriptions={selectedSubscriptions} />
          <ChangeProcessorBulkActionModal subscriptionIds={subscriptionIds} />
          <CancelSubscriptionsBulkActionModal subscriptionIds={subscriptionIds} />
          <BulkResumeSubscriptionsBulkActionModal subscriptionIds={subscriptionIds} />
          <UpdateShippingRateBulkActionModal subscriptions={selectedSubscriptions} />
          <UpdateQuantityBulkActionModal subscriptionIds={subscriptionIds} />
          <SetTagadaVaultBulkActionModal subscriptionIds={subscriptionIds} />
          <RetryPaymentBulkActionModal subscriptionIds={subscriptionIds} />
        </div>
      </DataTableActionBar>
    </>
  );
}
